import { useState, type JSX } from 'react';
import { userErrorMessage } from '../api/errors.js';
import { useI18n } from '../i18n/context.js';

/**
 * Добавление объекта в книгу: либо выбрать существующий из библиотеки,
 * либо завести новый одним полем. Библиотеки переиспользуемые (§5.10),
 * поэтому «создать» — не единственный путь.
 */
export function LibraryPicker<K extends string = never>({
  addLabel,
  createLabel,
  nameLabel,
  kinds,
  options,
  onAdd,
  onCreate,
}: {
  addLabel: string;
  createLabel: string;
  nameLabel: string;
  /**
   * Asked beside the name when a new item has a kind that changes how it is drawn — a
   * character: a silent default of "child" turned Grandma into a child until someone
   * noticed the field further down.
   */
  kinds?: {
    readonly label: string;
    readonly options: readonly { readonly value: K; readonly label: string }[];
  };
  /**
   * What is already in the library and not in this book. `detail` says where it is used, so
   * reusing reads as reusing: an unlabelled "—" menu next to a "Create" form did not say
   * which of the two made something new.
   */
  options: readonly {
    readonly id: string;
    readonly label: string;
    readonly detail?: string;
    /** Its drawing, so a reused character is recognised by face, not only by name. */
    readonly imageUrl?: string | undefined;
  }[];
  onAdd(id: string): void | Promise<unknown>;
  onCreate(name: string, kind: K | undefined): void | Promise<unknown>;
}): JSX.Element {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<K | ''>('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = (action: () => void | Promise<unknown>, done: () => void): void => {
    setPending(true);
    setError(null);
    void Promise.resolve()
      .then(action)
      .then(done)
      .catch((reason: unknown) => setError(userErrorMessage(reason, t)))
      .finally(() => setPending(false));
  };

  return (
    <div className="kitup-card grid gap-5 p-4">
      {options.length > 0 ? (
        <section className="grid gap-2" aria-labelledby="library-picker-reuse">
          <h2 id="library-picker-reuse" className="text-sm font-semibold">
            {t.library.picker.reuseTitle}
          </h2>
          <p className="text-xs text-[var(--kitup-color-muted)]">{t.library.picker.reuseHint}</p>
          <ul className="flex flex-wrap gap-2">
            {options.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  className="kitup-secondary-button flex items-center gap-2 text-left"
                  disabled={pending}
                  aria-label={`${addLabel}: ${option.label}`}
                  onClick={() =>
                    run(
                      () => onAdd(option.id),
                      () => undefined,
                    )
                  }
                >
                  {option.imageUrl ? (
                    <img
                      src={option.imageUrl}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-[var(--kitup-radius-sm)] object-cover"
                    />
                  ) : null}
                  <span className="grid justify-items-start gap-0.5">
                    <span>+ {option.label}</span>
                    {option.detail ? (
                      <span className="text-xs font-normal text-[var(--kitup-color-muted)]">
                        {option.detail}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <form
        className="grid gap-2"
        aria-labelledby="library-picker-new"
        onSubmit={(event) => {
          event.preventDefault();
          const normalizedName = name.trim();
          if (pending || normalizedName.length === 0 || (kinds && kind === '')) return;
          run(
            () => onCreate(normalizedName, kind === '' ? undefined : kind),
            () => {
              setName('');
              setKind('');
            },
          );
        }}
      >
        {options.length > 0 ? (
          <h2 id="library-picker-new" className="text-sm font-semibold">
            {t.library.picker.newTitle}
          </h2>
        ) : null}
        <div className="flex flex-wrap items-end gap-2">
          <label className="grid min-w-0 flex-1 basis-60 gap-1.5 text-sm font-medium">
            {nameLabel}
            <input
              id="library-picker-new-name"
              className="kitup-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          {kinds ? (
            <label className="grid gap-1.5 text-sm font-medium">
              {kinds.label}
              <select
                className="kitup-input"
                value={kind}
                required
                onChange={(event) => setKind(event.target.value as K | '')}
              >
                <option value="" disabled>
                  {t.library.picker.chooseKind}
                </option>
                {kinds.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="submit"
            className="kitup-primary-button"
            disabled={pending || name.trim().length === 0 || (kinds !== undefined && kind === '')}
          >
            {createLabel}
          </button>
        </div>
      </form>
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
